"use client";

import type { InfiniteData } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

type PaginationData = {
  total: number | null;
};

type UseMasterDetailDataOptions<_T, TPage = Record<string, unknown>> = {
  data: InfiniteData<TPage> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  paginationField?: string; // e.g., "pagination" or "jobsPagination"
  itemsField?: string; // e.g., "profiles" or "jobs"
};

export function useMasterDetailData<
  T extends { id: number },
  TPage = Record<string, unknown>,
>({
  data,
  isLoading,
  isFetching,
  hasNextPage,
  paginationField = "pagination",
  itemsField = "items",
}: UseMasterDetailDataOptions<T, TPage>) {
  // Combine isLoading and isFetching to show loading state when query is re-triggered
  const isLoadingData = isLoading || (isFetching && !data);

  // Track if we're showing detail view - start with first item shown
  const [showDetail, setShowDetail] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Flatten all pages into a single array, deduplicated by ID
  const items = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    const allItems = data.pages.flatMap((page) => {
      const fieldValue = page[itemsField as keyof typeof page];
      return (Array.isArray(fieldValue) ? (fieldValue as T[]) : []) ?? [];
    });
    // Deduplicate by ID to prevent duplicates across pages
    const seen = new Set<number>();
    return allItems.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }, [data?.pages, itemsField]);

  // Calculate displayed count based on actual number of items displayed
  const displayedCount = useMemo(() => items.length, [items.length]);

  // Get total count from API pagination (from first page)
  // If all pages are loaded (no more pages), use displayed count as total
  // Otherwise use API total
  const totalCount = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) {
      return null;
    }
    const firstPage = data.pages[0];
    const paginationFieldValue =
      firstPage?.[paginationField as keyof typeof firstPage];
    const pagination = paginationFieldValue as PaginationData | undefined;
    const apiTotal = pagination?.total ?? null;

    // If all pages are loaded (hasNextPage is false), use displayed count as total
    // This handles cases where deduplication reduces the count or API total is inaccurate
    if (!hasNextPage && apiTotal !== null) {
      return displayedCount;
    }

    return apiTotal;
  }, [data?.pages, hasNextPage, displayedCount, paginationField]);

  // Track previous items length to detect when data changes
  const prevItemsLengthRef = useRef<number>(0);

  // Initialize selectedId when items are loaded
  useEffect(() => {
    // Check if items array changed (new data loaded)
    const itemsChanged = prevItemsLengthRef.current !== items.length;

    // Update ref
    prevItemsLengthRef.current = items.length;

    // If items are loaded and (no item selected OR items changed after loading), select first item
    if (items.length > 0 && (!selectedId || (itemsChanged && !isLoadingData))) {
      const firstItemId = items[0]?.id || null;
      if (firstItemId && firstItemId !== selectedId) {
        setSelectedId(firstItemId);
        setShowDetail(true);
      }
    }
  }, [items, selectedId, isLoadingData]);

  // Handler for item selection - opens detail view but keeps sidebar open
  const handleSelect = (id: number) => {
    setSelectedId(id);
    setShowDetail(true);
  };

  return {
    items,
    displayedCount,
    totalCount,
    selectedId,
    showDetail,
    isLoadingData,
    handleSelect,
    setShowDetail,
  };
}
